import { pathNormalAt, pathPointAt } from "./axis/pathHelpers";
import { isPointInPolygon } from "./intersect2d";
import { segmentById } from "./planarGraphLookup";
import type { PlanarGraph, SegmentEntry, XzPoint } from "./types";

export type SegmentInteriorEdgeIndex = 1 | 3;
export type InteriorRings = readonly (readonly XzPoint[])[];

export function planarGraphInteriorRings(state: PlanarGraph): XzPoint[][] {
  return state.faces.interior.map((face) =>
    face.vertexIndices.map((index) => state.graph.vertices[index]!),
  );
}

/**
 * Hangi logical yüz (e1/e3) verilen ring'lerin içine bakıyor?
 * plus (+normal / e1) veya minus (−normal / e3).
 */
export function faceIndexTowardRings(
  entry: SegmentEntry,
  rings: readonly (readonly XzPoint[])[],
): SegmentInteriorEdgeIndex | undefined {
  if (rings.length === 0) return undefined;

  const center = pathPointAt(entry.spec.path, 0.5);
  const normal = pathNormalAt(entry.spec.path, 0.5);
  const distance =
    Math.max(entry.spec.upThickness, entry.spec.downThickness) + 0.03;
  const plus = {
    x: center.x + normal.x * distance,
    z: center.z + normal.z * distance,
  };
  const minus = {
    x: center.x - normal.x * distance,
    z: center.z - normal.z * distance,
  };
  const plusInside = rings.some((ring) => isPointInPolygon(plus, [...ring]));
  const minusInside = rings.some((ring) => isPointInPolygon(minus, [...ring]));
  if (plusInside === minusInside) return undefined;
  return plusInside ? 1 : 3;
}

export function interiorLogicalEdgeIndex(
  entry: SegmentEntry,
  interiorRings: readonly (readonly XzPoint[])[],
): SegmentInteriorEdgeIndex | undefined {
  return faceIndexTowardRings(entry, interiorRings);
}

export function segmentInteriorLogicalEdgeIndex(
  state: PlanarGraph,
  segmentId: string,
  interiorRings: InteriorRings = planarGraphInteriorRings(state),
): SegmentInteriorEdgeIndex | undefined {
  const entry = segmentById(state, segmentId);
  if (!entry) return undefined;
  return interiorLogicalEdgeIndex(entry, interiorRings);
}

/**
 * Duvar / opening ölçü yüzü: interior ring → daha kısa yüz → e1.
 * (wall preferredDimensionLogicalEdge ile aynı tercih)
 */
export function preferredHostFaceEdgeIndex(
  state: PlanarGraph,
  segmentId: string,
): SegmentInteriorEdgeIndex | null {
  const entry = segmentById(state, segmentId);
  if (!entry || entry.spec.placement) return null;

  const interior = faceIndexTowardRings(entry, planarGraphInteriorRings(state));
  if (interior) return interior;

  const len1 = Math.hypot(
    entry.outline.logicalEdges[1].end.x - entry.outline.logicalEdges[1].start.x,
    entry.outline.logicalEdges[1].end.z - entry.outline.logicalEdges[1].start.z,
  );
  const len3 = Math.hypot(
    entry.outline.logicalEdges[3].end.x - entry.outline.logicalEdges[3].start.x,
    entry.outline.logicalEdges[3].end.z - entry.outline.logicalEdges[3].start.z,
  );
  if (Math.abs(len1 - len3) < 1e-4) return 1;
  return len1 < len3 ? 1 : 3;
}
